import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' }
});

// Add auth token to requests
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data),
};

// Dashboard
export const dashboardAPI = {
  get: () => api.get('/dashboard'),
};

// Products
export const productsAPI = {
  list: (params) => api.get('/products', { params }),
  get: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  categories: () => api.get('/products/categories/list'),
  createCategory: (data) => api.post('/products/categories', data),
  manufacturers: () => api.get('/products/manufacturers/list'),
  createManufacturer: (data) => api.post('/products/manufacturers', data),
  lookupBarcode: (barcode) => api.get(`/products/barcode/${barcode}`),
};

// Inventory
export const inventoryAPI = {
  list: (params) => api.get('/inventory', { params }),
  batches: (productId, params) => api.get(`/inventory/batches/${productId}`, { params }),
  nearExpiry: (params) => api.get('/inventory/near-expiry', { params }),
  lowStock: () => api.get('/inventory/low-stock'),
  movements: (params) => api.get('/inventory/movements', { params }),
  adjust: (data) => api.post('/inventory/adjust', data),
  transfer: (data) => api.post('/inventory/transfer', data),
};

// Invoices
export const invoicesAPI = {
  list: (params) => api.get('/invoices', { params }),
  get: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  convert: (id, data) => api.post(`/invoices/${id}/convert`, data),
  recordPayment: (id, data) => api.post(`/invoices/${id}/payment`, data),
  stats: (params) => api.get('/invoices/stats/summary', { params }),
  // PDF endpoints
  downloadPDF: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  getPDFUrl: (id) => `${API_BASE}/invoices/${id}/pdf/view`,
};

// Purchases
export const purchasesAPI = {
  listOrders: (params) => api.get('/purchases/orders', { params }),
  getOrder: (id) => api.get(`/purchases/orders/${id}`),
  createOrder: (data) => api.post('/purchases/orders', data),
  listGRN: (params) => api.get('/purchases/grn', { params }),
  createGRN: (data) => api.post('/purchases/grn', data),
};

// Suppliers
export const suppliersAPI = {
  list: (params) => api.get('/suppliers', { params }),
  get: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
};

// Customers
export const customersAPI = {
  list: (params) => api.get('/customers', { params }),
  get: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
};

// Warehouses
export const warehousesAPI = {
  list: () => api.get('/warehouses'),
  create: (data) => api.post('/warehouses', data),
};

// Reports
export const reportsAPI = {
  gstr1: (params) => api.get('/reports/gstr1', { params }),
  gstr3b: (params) => api.get('/reports/gstr3b', { params }),
  stockValuation: (params) => api.get('/reports/stock-valuation', { params }),
  salesSummary: (params) => api.get('/reports/sales-summary', { params }),
};

// E-way Bills
export const ewayBillAPI = {
  list: (params) => api.get('/eway-bills', { params }),
  get: (id) => api.get(`/eway-bills/${id}`),
  validate: (invoiceId) => api.post(`/eway-bills/validate/${invoiceId}`),
  generate: (data) => api.post('/eway-bills/generate', data),
  cancel: (data) => api.post('/eway-bills/cancel', data),
  downloadJSON: (id) => api.get(`/eway-bills/download/${id}`, { responseType: 'blob' }),
};

export default api;
